import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { LocalizedText } from '../../types/LocalizedText';
import { useLanguages } from '../../hooks/useLanguages';
import { AppLanguage } from '../../types/Language';

export interface LocalizedFieldConfig {
  key: string;
  label: string;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
  htmlAllowed?: boolean;
  helperText?: string;
}

interface LocalizedFieldsTabsProps {
  fields: LocalizedFieldConfig[];
  values: Record<string, LocalizedText>;
  onChange: (values: Record<string, LocalizedText>) => void;
  sectionTitle?: string;
}

interface LocalizedTextFieldsProps {
  label: string;
  value: LocalizedText;
  onChange: (value: LocalizedText) => void;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
  helperText?: string;
  htmlAllowed?: boolean;
}

function getInitialTabIndex(languages: AppLanguage[], preferredLanguage: string): number {
  const preferredIndex = languages.findIndex(
    (language) => language.code === preferredLanguage || preferredLanguage.startsWith(language.code)
  );

  return preferredIndex >= 0 ? preferredIndex : 0;
}

function isLanguageComplete(
  languages: AppLanguage[],
  languageCode: string,
  fields: LocalizedFieldConfig[],
  values: Record<string, LocalizedText>
): boolean {
  return fields.every((field) => {
    const isFieldRequired = field.required !== false;
    const language = languages.find((item) => item.code === languageCode);
    const isLanguageRequired = language?.isRequired !== false;

    if (!isFieldRequired && !isLanguageRequired) {
      return true;
    }

    return Boolean(values[field.key]?.[languageCode]?.trim());
  });
}

export const LocalizedFieldsTabs: React.FC<LocalizedFieldsTabsProps> = ({
  fields,
  values,
  onChange,
  sectionTitle,
}) => {
  const { t, i18n } = useTranslation();
  const { activeLanguages, isLoading } = useLanguages();
  const [activeTab, setActiveTab] = useState(0);
  const hasInitializedTab = useRef(false);

  useEffect(() => {
    if (activeLanguages.length === 0) {
      return;
    }

    setActiveTab((currentTab) =>
      currentTab < activeLanguages.length ? currentTab : 0
    );
  }, [activeLanguages.length]);

  useEffect(() => {
    if (activeLanguages.length === 0 || hasInitializedTab.current) {
      return;
    }

    setActiveTab(getInitialTabIndex(activeLanguages, i18n.language));
    hasInitializedTab.current = true;
  }, [activeLanguages, i18n.language]);

  const activeLanguage = activeLanguages[activeTab];

  const updateFieldValue = (fieldKey: string, languageCode: string, text: string) => {
    onChange({
      ...values,
      [fieldKey]: {
        ...(values[fieldKey] ?? {}),
        [languageCode]: text,
      },
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          {t('localizedText.loadingLanguages')}
        </Typography>
      </Box>
    );
  }

  if (activeLanguages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('localizedText.noLanguagesConfigured')}
      </Typography>
    );
  }

  return (
    <Box>
      {sectionTitle && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {sectionTitle}
        </Typography>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {activeLanguages.map((language, index) => {
          const complete = isLanguageComplete(activeLanguages, language.code, fields, values);

          return (
            <Tab
              key={language.code}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{language.label}</span>
                  {!complete && language.isRequired !== false && (
                    <Typography component="span" color="error.main" sx={{ fontSize: '1rem', lineHeight: 1 }}>
                      *
                    </Typography>
                  )}
                </Box>
              }
              id={`localized-tab-${index}`}
              aria-controls={`localized-tabpanel-${index}`}
            />
          );
        })}
      </Tabs>

      {activeLanguage && (
        <Box
          role="tabpanel"
          id={`localized-tabpanel-${activeTab}`}
          aria-labelledby={`localized-tab-${activeTab}`}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {fields.map((field) => {
            const fieldValue = values[field.key]?.[activeLanguage.code] ?? '';

            return (
              <Box key={field.key}>
                <TextField
                  label={field.label}
                  value={fieldValue}
                  onChange={(event) =>
                    updateFieldValue(field.key, activeLanguage.code, event.target.value)
                  }
                  fullWidth
                  required={field.required || activeLanguage.isRequired !== false}
                  multiline={field.multiline}
                  rows={field.multiline ? field.rows ?? 4 : undefined}
                  InputLabelProps={field.multiline ? { shrink: true } : undefined}
                  inputProps={
                    field.htmlAllowed
                      ? { style: { fontFamily: 'monospace', fontSize: '0.875rem' } }
                      : undefined
                  }
                  helperText={
                    field.helperText ||
                    (field.htmlAllowed ? t('localizedText.htmlAllowed') : undefined) ||
                    (activeLanguage.isRequired !== false
                      ? t('localizedText.requiredLanguage')
                      : undefined)
                  }
                />

                {field.htmlAllowed && fieldValue.trim() && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('localizedText.htmlPreview')}
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        '& img': { maxWidth: '100%', height: 'auto' },
                        '& a': { color: 'primary.main' },
                        '& p': { mt: 0, mb: 1, '&:last-child': { mb: 0 } },
                        '& ul, & ol': { pl: 3, my: 1 },
                      }}
                      dangerouslySetInnerHTML={{ __html: fieldValue }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const LocalizedTextFields: React.FC<LocalizedTextFieldsProps> = ({
  label,
  value,
  onChange,
  multiline = false,
  rows = 4,
  required = false,
  helperText,
  htmlAllowed = false,
}) => {
  const fields = useMemo<LocalizedFieldConfig[]>(
    () => [
      {
        key: 'value',
        label,
        multiline,
        rows,
        required,
        htmlAllowed,
        helperText,
      },
    ],
    [label, multiline, rows, required, htmlAllowed, helperText]
  );

  return (
    <LocalizedFieldsTabs
      fields={fields}
      values={{ value }}
      onChange={(updatedValues) => onChange(updatedValues.value ?? {})}
    />
  );
};

export default LocalizedTextFields;
